import { useSyncExternalStore } from 'react';
import { DiceExpression, describeRoll, isNaturalMax, isNaturalMin, secureRandom, type RollResult } from '@realmery/realm-core';
import { settings } from '../settings.ts';

/**
 * The Dice Roller's history and saved rolls (docs/PARITY-STEP-2-PLAN.md section 3), as the
 * desktop's `DiceService` and `DicePresetStore`: every roll made anywhere goes into one
 * history, newest first, up to Settings > Rolls kept in the History; saved rolls start as
 * the desktop's five. Both are kept on this device, every read and write guarded.
 */

export interface RollRecord {
  /** Who or what it was for ("Arion Valen: Athletics"), or empty. */
  readonly label: string;
  readonly expression: string;
  /** A Java long, as text. */
  readonly total: string;
  /** `describe()`: the breakdown and the total. */
  readonly text: string;
  readonly naturalMax: boolean;
  readonly naturalMin: boolean;
  /** Whether a single group counted successes, so the total is how many. */
  readonly successes: boolean;
  readonly at: number;
}

export interface Preset {
  readonly name: string;
  readonly expression: string;
}

/** `DicePresetStore.DEFAULTS`. */
export const DEFAULT_PRESETS: readonly Preset[] = [
  { name: 'Ability score', expression: '4d6kh3' },
  { name: 'Advantage', expression: '2d20kh1' },
  { name: 'Disadvantage', expression: '2d20kl1' },
  { name: 'Fate', expression: '4dF' },
  { name: 'Percentile', expression: 'd%' },
];

const HISTORY_KEY = 'realmery.diceHistory';
const PRESETS_KEY = 'realmery.dicePresets';

function read<T>(key: string, check: (value: unknown) => value is T, fallback: T): T {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    return check(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Kept until the page closes.
  }
}

const isRecord = (r: unknown): r is RollRecord =>
  !!r && typeof r === 'object' && typeof (r as RollRecord).expression === 'string' && typeof (r as RollRecord).text === 'string' && typeof (r as RollRecord).at === 'number';
const isHistory = (v: unknown): v is RollRecord[] => Array.isArray(v) && v.every(isRecord);
const isPresets = (v: unknown): v is Preset[] =>
  Array.isArray(v) && v.every((p) => !!p && typeof p.name === 'string' && typeof p.expression === 'string' && p.name.length <= 100 && p.expression.length <= 500);

let history: RollRecord[] = read(HISTORY_KEY, isHistory, []);
let presets: Preset[] = read(PRESETS_KEY, isPresets, [...DEFAULT_PRESETS]);
let version = 0;
const listeners = new Set<() => void>();
const changed = () => {
  version++;
  listeners.forEach((l) => l());
};

export function recordRoll(label: string, result: RollResult): RollRecord {
  const record: RollRecord = {
    label,
    expression: result.expression,
    total: String(result.total),
    text: describeRoll(result),
    naturalMax: result.groups.some(isNaturalMax),
    naturalMin: result.groups.some(isNaturalMin),
    successes: result.groups.length === 1 && result.groups[0]!.countsSuccesses,
    at: Date.now(),
  };
  history = [record, ...history].slice(0, settings()['dice.historyLimit']);
  write(HISTORY_KEY, history);
  changed();
  return record;
}

/** Rolls with the browser's secure random numbers and records it; throws a DiceError the roller shows. */
export function roll(expression: string, label = ''): RollRecord {
  return recordRoll(label, DiceExpression.parse(expression).roll(secureRandom));
}

export function clearHistory(): void {
  history = [];
  write(HISTORY_KEY, history);
  changed();
}

export function savePresets(next: readonly Preset[]): void {
  presets = [...next];
  write(PRESETS_KEY, presets);
  changed();
}

export function useDice(): { history: readonly RollRecord[]; presets: readonly Preset[] } {
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
  );
  return { history, presets };
}

/** For tests: reread storage as a fresh page would. */
export function reloadDice(): void {
  history = read(HISTORY_KEY, isHistory, []);
  presets = read(PRESETS_KEY, isPresets, [...DEFAULT_PRESETS]);
  changed();
}
